import React, { ChangeEvent, useRef, useState } from 'react';
import useLocales from 'hooks/useLocales';
import { useSelector } from 'react-redux';
import Image from 'components/image/Image';
import Button from 'components/Button/Index';
import { useSnackbar } from 'notistack';
import { isValidURL } from 'helpers/isValidURL';

import './Index.scss';

// Update the isValidURL helper function to handle non-string inputs
const safeIsValidURL = (value: any): boolean => {
  // Only call isValidURL if value is a string
  if (typeof value === 'string') {
    return isValidURL(value);
  }
  return false;
};

const ImagePicker = ({
  setLogo,
  logo,
  isVideo,
  setSizes,
  max = 5,
  setFiledValue
}: any): JSX.Element => {
  const { t } = useLocales();
  const fileRef = useRef(null);
  const [files, setFiles] = useState<any>();
  const user = useSelector((state: any) => state.user).user;
  const uploadedFiles = useSelector((state: any) => state.file.files);
  const { enqueueSnackbar } = useSnackbar();

  const handleRemoveImage = (item: any) => {
    if (!user) {
      return;
    }

    // Reset file input
    if (fileRef.current) {
      (fileRef.current as HTMLInputElement).value = '';
    }

    // Handle single file case
    if (max === 1) {
      setLogo(null);
      setFiles(null);
      setFiledValue && setFiledValue(null);
      return;
    }

    // Handle multiple files case
    setLogo((prevState: any) => {
      if (!Array.isArray(prevState)) {
        return null;
      }
      return prevState.filter((image: any, index: number) => index !== item.id);
    });
    setFiledValue && setFiledValue(null);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles?.length > 0) {
      handleImageChosen(droppedFiles);
    }
  };

  const handleImageChosen = (files: FileList): void => {
    if (files?.[0].size < 1 * 1024 * 1024) {
      setFiles(files);
      if (max === 1) {
        setLogo(files?.[0]);
        setFiledValue && setFiledValue(files?.[0]);
      } else {
        setLogo((prevState: any) =>
          Array.isArray(prevState) ? [...prevState, files?.[0]] : [files?.[0]]
        );
        setFiledValue && setFiledValue(files?.[0]);
      }
    } else {
      enqueueSnackbar(t('Image is too large'), {
        variant: 'warning'
      });
    }
  };

  const chooseFile = () => {
    if (fileRef.current) {
      (fileRef.current as HTMLInputElement).click();
    }
  };

  const handleUpload = (e: ChangeEvent<HTMLInputElement>): void => {
    const { files } = e.target;
    if (files) handleImageChosen(files);
  };

  const isDisabled = () => {
    if (max === 1) {
      return !!logo;
    }
    if (Array.isArray(logo)) {
      return logo.length >= max;
    }
    return false;
  };

  return (
    <div className="sharedContainer">
      <div className="images">
        {logo &&
          !setSizes &&
          (Array.isArray(logo) ? (
            logo.map((item: any, index: number) => (
              <div key={index} className="image">
                <span
                  className="trash-shared"
                  onClick={() => handleRemoveImage({ id: index })}
                >
                  <Image name="shared-remove.svg" width={36} height={36} />
                </span>
                <Image
                  url={safeIsValidURL(item) ? item : URL.createObjectURL(item)}
                  className="shared-images"
                />
              </div>
            ))
          ) : (
            <div className="image single-image">
              <span
                className="trash-shared"
                onClick={() => handleRemoveImage({})}
              >
                <Image name="shared-remove.svg" width={36} height={36} />
              </span>
              <Image
                url={safeIsValidURL(logo) ? logo : URL.createObjectURL(logo)}
                className="shared-images"
              />
            </div>
          ))}
      </div>
      <div className={`about-side`}>
        <div
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`drag-and-drop_image ${isDisabled() ? 'disabled' : ''}`}
        >
          <div className="texts">
            <Image
              className="upload"
              name="upload-file.svg"
              width={40}
              height={40}
            />

            <span className="drag-title">
              {t('Drag and drop your')} {!isVideo ? 'files' : 'video'}{' '}
              {t('here')}
            </span>
            <span className="supported">
              {t('Files supported:')}{' '}
              {!isVideo ? 'JPG, PNG' : 'MP4, AVI, WMV, AVCHD'}
            </span>
            <span className="or">{t('Or')}</span>
            <Button
              t={t}
              onClick={chooseFile}
              title="Browse file"
              className="browse"
            />
          </div>
        </div>
      </div>
      <input
        ref={fileRef}
        className="file"
        type="file"
        accept="image/png, image/jpeg"
        onChange={handleUpload}
        multiple={max !== 1}
      />
    </div>
  );
};

export default ImagePicker;
